import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import Button from 'components/styleguide/button/Button';
import IconButton from 'components/styleguide/icon_button/IconButton';

const styles = require('./Sheet.module.scss');

const FOCUSABLE_SELECTOR = 'button, [href]';

const randomId = (prefix: string) =>
  `${prefix}-${Math.random().toString(36).slice(2, 9)}`;

export type SheetContext = 'talent' | 'company' | 'neutral';

export interface SheetProps {
  cancelButtonText?: string;
  children: React.ReactNode;
  className?: string;
  context?: SheetContext;
  continueButtonText?: string;
  description?: string | React.ReactNode;
  isOpen?: boolean;
  // Rendered above the content, inside the sheet, for messages the user must be
  // able to see and act on while the sheet is open.
  notice?: React.ReactNode;
  onBack?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
  onContinue?: (data?: any) => void;
  showActions?: boolean;
  showCancelButton?: boolean;
  title: string | React.ReactNode;
  triggerId?: string;
}

const Sheet: React.FC<SheetProps> = ({
  cancelButtonText = 'Cancel',
  children,
  className,
  context = 'neutral',
  continueButtonText = 'Continue',
  description,
  isOpen: isOpenProp = true,
  notice,
  onBack,
  onCancel,
  onClose,
  onContinue,
  showActions = true,
  showCancelButton = true,
  title,
  triggerId,
}) => {
  const [isOpen, setIsOpen] = useState(isOpenProp);
  const [isClosing, setIsClosing] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [titleId] = useState(() => randomId('sheet-title'));

  const handleClose = useCallback(() => {
    setIsClosing(true);
  }, []);

  const handleAnimationEnd = useCallback(() => {
    if (!isClosing) return;

    setIsOpen(false);
    setIsClosing(false);
    onClose?.();
  }, [isClosing, onClose]);

  const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) handleClose();
  }, [handleClose]);

  useEffect(() => {
    setIsOpen(isOpenProp);
  }, [isOpenProp]);

  // Open from a non-React trigger (Rails-rendered button etc.).
  useEffect(() => {
    if (!triggerId) return undefined;

    const trigger = document.getElementById(triggerId);
    if (!trigger) return undefined;

    const handleOpen = () => setIsOpen(true);
    trigger.addEventListener('click', handleOpen);
    return () => trigger.removeEventListener('click', handleOpen);
  }, [triggerId]);

  // Body scroll lock + focus trap + restore.
  useEffect(() => {
    if (!isOpen) return undefined;

    previousFocusRef.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => sheetRef.current?.focus());

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusable = sheetRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      previousFocusRef.current?.focus();
    };
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return (
    <div
      className={classNames(styles.overlay, className, { [styles.closing]: isClosing })}
      onClick={handleBackdropClick}
    >
      <div
        ref={sheetRef}
        className={classNames(styles.sheet, { [styles.closing]: isClosing })}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onAnimationEnd={handleAnimationEnd}
      >
        <div className={styles.header}>
          {onBack && (
            <IconButton
              type="terceary"
              size="S"
              context="neutral"
              iconName="arrowLeft"
              onClick={onBack}
              aria-label="Back"
            />
          )}
          <div className={styles.titleGroup}>
            <h2 id={titleId} className={styles.title}>{title}</h2>
            {description && <p className={styles.description}>{description}</p>}
          </div>
          <IconButton
            type="terceary"
            size="S"
            context="neutral"
            iconName="x"
            onClick={handleClose}
            aria-label="Close"
          />
        </div>

        <div className={styles.content}>
          {notice && <div className={styles.notice}>{notice}</div>}
          {children}
        </div>

        {showActions && (
          <div className={styles.footer}>
            {showCancelButton && (
              <Button
                type="secondary"
                size="M"
                onClick={onCancel || handleClose}
                className={styles.actionButton}
              >
                {cancelButtonText}
              </Button>
            )}
            <Button
              type="primary"
              size="M"
              onClick={onContinue}
              className={styles.actionButton}
              context={context}
            >
              {continueButtonText}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sheet;
